import { useEffect, type Dispatch, type SetStateAction } from 'react';
import {
  ArrowsRightLeftIcon,
  ArrowTopRightOnSquareIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import SharedButton from '../../../../global/Button/Button';
import SharedBadge from '@/global/Badge/Badge';
import { BottomDetailValueWithTooltip } from './BottomDetailValueWithTooltip';
import {
  DetailGroup,
  getRowDetailContextLabel,
  normalizeDetailValue,
  RIGHT_SIDEBAR_COLLAPSED_WIDTH_PX,
  SHOW_HISTORIAL_SCORE,
} from '../utils/searchResultsHelpers';
import TabSelector from '@/global/TabSelector/TabSelector';
import { HistoricalScoreTabContent } from './HistoricalScoreTabContent';

interface RelationSummary {
  leftEntity: string;
  rightEntity: string;
  showRelationArrow: boolean;
  diseaseType: string;
  score: string;
  pmids: string;
  chem: string;
  variant: string;
}

interface BottomDetailPanelProps {
  bottomDetailRow: any;
  bottomDetailHeight: number;
  groupedBottomDetails: DetailGroup[];
  relationSummary: RelationSummary;
  source: any;
  tab: any;
  isSidebarOpen: boolean;
  sidebarWidth: number | string;
  isDesktop: boolean;
  searchParams: any;
  setSearchParams: (params: any) => void;
  onClose: () => void;
  startBottomResize: (startY: number) => void;
  bottomDetailTab: 'summary' | 'historical';
  setBottomDetailTab: (tab: 'summary' | 'historical') => void;
  setBottomDetailHeight?: Dispatch<SetStateAction<number>>;
  ensureTablesViewForDrilldown?: () => void;
}

export function BottomDetailPanel({
  bottomDetailRow,
  bottomDetailHeight,
  groupedBottomDetails,
  relationSummary,
  source,
  tab,
  isSidebarOpen,
  sidebarWidth,
  isDesktop,
  searchParams,
  setSearchParams,
  onClose,
  startBottomResize,
  bottomDetailTab,
  setBottomDetailTab,
  setBottomDetailHeight,
  ensureTablesViewForDrilldown,
}: BottomDetailPanelProps) {
  useEffect(() => {
    if (!bottomDetailRow || !setBottomDetailHeight || isDesktop || bottomDetailTab !== 'historical') {
      return;
    }
    const target = Math.min(700, Math.max(300, Math.round(window.innerHeight * 0.5)));
    setBottomDetailHeight(prev => (prev < target ? target : prev));
  }, [bottomDetailRow, bottomDetailTab, isDesktop, setBottomDetailHeight]);

  if (!bottomDetailRow) return null;

  const rightOffset = isDesktop ? (isSidebarOpen ? sidebarWidth : RIGHT_SIDEBAR_COLLAPSED_WIDTH_PX) : 0;
  const showHistoricalScoreTab =
    (SHOW_HISTORIAL_SCORE && String(tab ?? '').toUpperCase() === 'GDA') ||
    String(tab ?? '').toUpperCase() === 'VDA';

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-30 flex flex-col overflow-hidden border-t border-slate-200 bg-white shadow-[0_-8px_24px_rgba(0,0,0,0.08)]"
      style={{ height: bottomDetailHeight, right: rightOffset, transition: 'right 0.3s ease-out' }}
    >
      <div className="shrink-0 border-b border-slate-200 bg-gradient-to-r from-pink-50 to-white px-2 py-1.5 sm:px-4 sm:py-2">
        <div
          className="mx-auto flex w-fit cursor-grab items-center justify-center gap-2 px-3 py-1 text-center text-xs font-medium text-slate-600 transition-colors hover:bg-slate-100 active:bg-slate-200"
          onMouseDown={event => {
            event.preventDefault();
            startBottomResize(event.clientY);
          }}
          onTouchStart={event => {
            if (!event.touches.length) return;
            startBottomResize(event.touches[0].clientY);
          }}
          role="separator"
          aria-orientation="horizontal"
          aria-label="Drag to resize detail panel"
          title="Drag to resize"
        >
          <span className="drag-handle" style={{ rotate: '90deg' }}>
            ⋮⋮
          </span>
        </div>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-2">
          <div className="flex min-w-0 items-start gap-2 sm:items-center">
            <span className="shrink-0 rounded-xl bg-pink p-2 sm:mr-2">
              <SparklesIcon className="h-5 w-5 text-white" />
            </span>
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-xs font-normal uppercase tracking-widest text-gray-400">
                {getRowDetailContextLabel(tab as string)}
              </span>
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <span className="text-base font-semibold uppercase sm:text-lg">
                  {relationSummary.leftEntity}
                </span>
                {relationSummary.showRelationArrow && (
                  <>
                    <ArrowsRightLeftIcon className="h-4 w-4 shrink-0 text-gray-400" />
                    <span className="text-xs font-normal sm:text-sm">{relationSummary.rightEntity}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <SharedBadge
              text={`Source: ${source}`}
              icon={<SparklesIcon className="mr-1 h-4 w-4" />}
              variant="primaryLight"
            />
          </div>
          <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3 sm:border-t-0 sm:pt-0 sm:justify-end">
            <SharedButton
              type="button"
              variant="primaryOutline"
              size="xs"
              iconRight={<ArrowTopRightOnSquareIcon className="mb-1 h-4 w-4" />}
              onClick={() => {
                const nextParams = new URLSearchParams(searchParams as any);
                nextParams.set('fullRecord', '1');
                setSearchParams(nextParams);
              }}
            >
              <span className="hidden sm:inline">Open full record</span>
              <span className="sm:hidden">Full record</span>
            </SharedButton>
            <XMarkIcon
              className="mb-1 h-6 w-6 shrink-0 cursor-pointer sm:h-4 sm:w-4"
              strokeWidth={2}
              onClick={onClose}
            />
          </div>
        </div>
      </div>
      {showHistoricalScoreTab && (
        <div
          className="shrink-0 border-b border-slate-100 bg-white px-2 py-1.5 sm:px-3 sm:py-2"
          role="tablist"
          aria-label="Row detail sections"
        >
          <TabSelector
            options={[
              { label: 'Summary', value: 'summary' },
              { label: 'Historical score', value: 'historical' },
            ]}
            variant="primary"
            size="2xs"
            value={bottomDetailTab}
            onChange={v => setBottomDetailTab(v as 'summary' | 'historical')}
          />
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto p-2 sm:p-3">
        {showHistoricalScoreTab && bottomDetailTab === 'historical' ? (
          <HistoricalScoreTabContent
            row={bottomDetailRow}
            source={String(source)}
            tab={String(tab)}
            onClosePanel={onClose}
            ensureTablesViewForDrilldown={ensureTablesViewForDrilldown}
          />
        ) : (
          <div className="min-w-0 grid grid-cols-1 gap-3 xl:grid-cols-3">
            {groupedBottomDetails.map(group => (
              <section
                key={`bottom-${group.title}`}
                className="min-w-0 rounded-xl border border-slate-200 pt-0 pb-3"
              >
                <h3 className="mb-2 flex min-h-[2.25rem] items-center justify-between gap-2 rounded-t-xl border-b border-slate-200 bg-gradient-to-r from-pink-50 to-white px-3 py-2 text-xs font-semibold uppercase tracking-widest text-slate-600">
                  <span className="min-w-0 truncate">• {group.title}</span>
                  <span
                    className="shrink-0 tabular-nums font-light text-gray-500"
                    aria-label={`${group.entries.length} fields in this group`}
                    style={{ fontSize: '0.7rem' }}
                  >
                    {group.entries.length}
                  </span>
                </h3>
                <div className="space-y-1 px-3">
                  {group.entries.map(entry => (
                    <div
                      key={`${group.title}-${entry.key}`}
                      className="flex min-w-0 items-center justify-between gap-2 text-xs leading-[1.1]"
                    >
                      <span
                        className="w-[44%] shrink-0 overflow-hidden text-ellipsis whitespace-nowrap text-slate-500 tracking-wide"
                        title={entry.displayLabel}
                      >
                        {entry.displayLabel}
                      </span>
                      <BottomDetailValueWithTooltip text={normalizeDetailValue(entry.value, entry.key)} />
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
